// App.js
import logo from './logo.svg';
import './App.css';
import { BrowserRouter as Router, Navigate, useLocation, useNavigate, Route, Routes, matchPath } from 'react-router-dom';
import { routes } from './routes/routes';
import AppHeader from './header/header';
import { useEffect, useMemo, useRef, useState } from 'react';
import { UploadindexdbRecording } from './offlinestreaming/uploadrecordings';
import { unrestrictedRoutes } from './permisiions';
import { useDispatch } from 'react-redux';
import { clearConfig, fetchUserConfig, setConfig } from './reduxtoolkit/userconfigslice';
import api from './api/api';
import { CircularProgress, Box, Typography, Paper } from '@mui/material';
import { clearPrivileges, setHomescreen, setPayment, setPrivileges, setQa, setRpm, setTaskrpm } from './reduxtoolkit/userPrivileges';
import NotAllowedPage from './utils/reactpages/not allowed';
import GlobalToast from './utils/mui/globaltoast';
import { RecordingProvider } from './context/RecordingContext';
import FloatingRecorder from './components/FloatingRecorder/FloatingRecorder';
import OrderReviewHost from "./components/OrderReview/OrderReviewHost";
import LiveOrdersHost from "./components/OrderReview/LiveOrdersHost";
import VisitConsentHost from './components/VisitConsent/VisitConsentHost';
import AttendanceCorrectionHost from './components/AttendanceCorrection/AttendanceCorrectionHost';
import useAutoUpdate from './utils/useAutoUpdate';
import DeferredNavigationNotice from './components/DeferredNavigationNotice';
import { WORKING_LOCATION_CHANGED } from './header/WorkingLocationSelect';
import { eventBus } from './utils/eventBus';
import { navigationBoundary } from './utils/deferredNavigation';
import { getPracticeId, practiceAppName } from './config/practice';
import FormKioskHost, { KioskRelockScreen } from './forms/patient/FormKioskHost';
import { startCareiqN3Flag } from './utils/careiqN3Flag';
import { clearKioskRelock, installKioskPageGuard, isStaffSessionLocked, kioskRelockPending } from './forms/patient/kioskLock';
import { routeAllowed } from './config/screenPrivileges';
// Form Wizard tablet mode: a staff page of this tab restored from the
// back/forward cache (Back / iPad edge swipe) while a patient holds the device
// reloads into the "return the device" screen (kioskLock.js).
installKioskPageGuard();
const App = () => {
  return (
    <div className="App bg-[#faf9fa] h-full">
      <RecordingProvider>
        <Router basename={process.env.PUBLIC_URL || "/"}>
          <AppContent />
        </Router>
        <FloatingRecorder />
        {/* Two-step consent before recording on anything but today's visit. */}
        <VisitConsentHost />
        {/* The end-of-visit order review screen (enabled providers only; nothing ordered without Confirm). */}
        <OrderReviewHost />
        {/* Opens that review after the stop whichever tab is open (or a toast off the chart); live Orders card visits. */}
        <LiveOrdersHost />
        {/* Why a reload / sign-in is waiting for the recording to end. */}
        <DeferredNavigationNotice />
      </RecordingProvider>
    </div>
  );
};

export const invalidroutes = ["/auth"];

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { updateReady, reloadNow } = useAutoUpdate();
  const dispatch = useDispatch();
  const [currentPrivileges, setCurrentPrivileges] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentPath = location.pathname;
  // Form Wizard patient page (public): no staff update banner on it.
  const isPatientFormRoute = location.pathname.startsWith("/f/");
  const practiseid = getPracticeId();
  const accessToken = localStorage.getItem('auth');
  const isCalledRef = useRef(false);
 const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const allowToken = queryParams.get('allowtoken'); // special flow token
  const headerParam = queryParams.get('hdr')
  const fetchUser = async () => {
     const isUnrestricted = unrestrictedRoutes.some(route => currentPath.startsWith(route));
    
    try {
       if (isUnrestricted) {
      dispatch(clearConfig());
      dispatch(clearPrivileges())
      return
    }
      const userRes = await api.get('/auth/me');
      const userPermissions = userRes?.permissions
        ? JSON.parse(userRes.permissions)
        : [];
      dispatch(setConfig(userRes));
      // CareIQ N3 rollout flag: asked after login, re-checked every 5 min
      // (9928, and 7825 for its N3-only CareIQ tab; no request elsewhere).
      // utils/careiqN3Flag.js.
      startCareiqN3Flag();
      // Fetch privileges from backend
      const getAllPrivileges = await api.get(`/v1/${practiseid}/privileges/get`);
 if (getAllPrivileges.status === 200) {
  const allPrivileges = getAllPrivileges.data;

  // Side Bar
  const sidebarPrivileges = allPrivileges.filter(x => x.page === "Side Bar");
  const userSidebarPrivileges = sidebarPrivileges.filter(priv =>
    userPermissions.includes(priv.id.toString())
  );
  dispatch(setPrivileges(userSidebarPrivileges.map(x => x.privilege)));
  setCurrentPrivileges(userSidebarPrivileges.map(x => x.privilege));

  // Home Screen
  const homescreenPrivileges = allPrivileges.filter(x => x.page === "Home Screen");
  const userHomescreenPrivileges = homescreenPrivileges.filter(priv =>
    userPermissions.includes(priv.id.toString())
  );
  dispatch(setHomescreen(userHomescreenPrivileges.map(x => x.privilege)));
// Payments

const paymentPrivileges = allPrivileges.filter(x => x.page === "Payment");
const userPaymentPrivileges = paymentPrivileges.filter(priv =>
  userPermissions.includes(priv.id.toString())
);
// QA
const qaPrivileges = allPrivileges.filter(x => x.page === "QA");

const userQaPrivileges = qaPrivileges.filter(priv =>
  userPermissions.includes(priv.id.toString())
);

dispatch(setQa(userQaPrivileges.map(x => x.privilege)));
dispatch(setPayment(userPaymentPrivileges.map(x => x.privilege)));
  // Tasks
  const taskrpmPrivileges = allPrivileges.filter(x => x.page === "Tasks");

  const userTaskrpmPrivileges = taskrpmPrivileges.filter(priv =>
    userPermissions.includes(priv.id.toString())
  );
  dispatch(setTaskrpm(userTaskrpmPrivileges.map(x => x.privilege)));

  // RPM
  const rpmPrivileges = allPrivileges.filter(x => x.page === "RPM");
  const userRpmPrivileges = rpmPrivileges.filter(priv =>
    userPermissions.includes(priv.id.toString())
  );
  dispatch(setRpm(userRpmPrivileges.map(x => x.privilege)));
}

    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [location.pathname]);

  useEffect(() => {
    const isUnrestricted = unrestrictedRoutes.some(route => currentPath.startsWith(route));
    if (isUnrestricted) {
      dispatch(clearConfig());
    } else {
      // dispatch(fetchUserConfig());
    }
  }, [dispatch, currentPath]);

  // useEffect(() => {
  //   if (!isCalledRef.current) {
  //     isCalledRef.current = true;
  //     UploadindexdbRecording();
  //   }
  // }, []);
useEffect(() => {
  // Always call on page reload
  UploadindexdbRecording();

  const handleOnline = () => {
    // Call again when internet comes back
    UploadindexdbRecording();
  };

  window.addEventListener("online", handleOnline);

  return () => {
    window.removeEventListener("online", handleOnline);
  };
}, []);

  // A location switch during a recording does not reload the page (that would
  // end the recording). With the chart closed, remount the routed page instead
  // so it refetches for the new location; with the chart open (it lives on
  // /landing) the switcher refreshes the dashboard under it and leaves the
  // chart alone. No remount while a screen holds a local recording (Edit with
  // Voice in Note Review, a voice message): it would end that recording.
  const [routesEpoch, setRoutesEpoch] = useState(0);
  useEffect(() => {
    const onLocationChanged = (payload) => {
      if (!payload?.chartOpen && !payload?.localRecording) setRoutesEpoch((n) => n + 1);
    };
    eventBus.on(WORKING_LOCATION_CHANGED, onLocationChanged);
    return () => {
      eventBus.off(WORKING_LOCATION_CHANGED, onLocationChanged);
    };
  }, []);

  // A page change is a natural boundary: a reload / sign-in queued during a
  // recording (utils/deferredNavigation) runs here if nothing is busy any
  // more, the chart is closed and nobody is typing. Not on the first render.
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;
    navigationBoundary('route');
  }, [location.pathname]);

  // Redirect if restricted route & no auth
  const isUnrestrictedRoute = unrestrictedRoutes.some(route =>
    location.pathname.startsWith(route)
  );
  // Form Wizard tablet mode: this tab was reloaded (or duplicated) while a
  // patient held the device. The staff sign-in was in memory only and is gone;
  // show "return the device" (not the staff app, not the sign-in form).
  if (!isPatientFormRoute && kioskRelockPending()) {
    return (
      <KioskRelockScreen
        onStaffSignIn={() => {
          clearKioskRelock();
          navigate('/login', { replace: true });
        }}
      />
    );
  }
  if (!isUnrestrictedRoute && !accessToken) {
    return <Navigate to="/login" />;
  }

  // Show loader until privileges are fetched
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not allowed component (full-page, no header)

  // Function to map and render routes with privilege checks
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.path) {
        // If privilege required, check it
        if (!routeAllowed(route, currentPrivileges)) {
          return <Route key={route.path} path={route.path} element={<NotAllowedPage/>} />;
        }
        return <Route path={route.path} element={<route.component />} key={route.path} />;
      }
      return <Route path={route.path} element={<NotAllowedPage />} key={route.path} />;
    });

  // Find current route & check privileges for header
  const currentRoute = routes.find(route => matchPath(route.path, location.pathname));
  const hasPrivilege =
    !currentRoute || routeAllowed(currentRoute, currentPrivileges);
  const showHeader = currentRoute && currentRoute.showHeader && hasPrivilege;
// The public intake short-link pages never show the staff header (they hid it
// through ?allowtoken= before; the API no longer treats that token as a login).
const isIntakeShortLinkPage = ["/onboard-odessa", "/onboard-frisco", "/onboard-code"].some((p) => location.pathname.startsWith(p));
const shouldShowHeader = showHeader && !isIntakeShortLinkPage && (!allowToken || (allowToken && headerParam));
  return (
    <>
      {shouldShowHeader&& <AppHeader />}
       <GlobalToast />
       {updateReady && !isPatientFormRoute && (
         <div
           style={{
             position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
             zIndex: 9999, background: "#16294F", color: "#fff",
             padding: "10px 16px", borderRadius: 999, boxShadow: "0 6px 20px rgba(0,0,0,.25)",
             display: "flex", alignItems: "center", gap: 12, fontSize: 14,
           }}
         >
           <span>A new version of {practiceAppName()} is available.</span>
           <button
             onClick={reloadNow}
             style={{
               background: "#2E6EE2", color: "#fff", border: "none",
               padding: "6px 14px", borderRadius: 999, cursor: "pointer", fontWeight: 600,
             }}
           >
             Reload now
           </button>
         </div>
       )}
      {/* Automatic clock-out correction prompt (attendance, owner 10-19): signed-in staff pages only. */}
      {!isUnrestrictedRoute && !isPatientFormRoute && !isStaffSessionLocked() && <AttendanceCorrectionHost />}
      {/* Form Wizard tablet mode (staff device): listens for "formwiz:kiosk". */}
      {(!isUnrestrictedRoute || (isStaffSessionLocked() && !isPatientFormRoute)) && <FormKioskHost />}
      <Routes key={routesEpoch}>
        {getRoutes(routes)}
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="*" element={<>Page not available</>} />
      </Routes>
    </>
  );
}

export default App;
